import { useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { auth } from '../config/firebase.config'
import {
  subscribeToStaffEquipmentAlerts,
  unsubscribeFromStaffEquipmentAlerts,
  getFCMToken,
} from '@hapana/shared/web/fcm.service'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import useSiteStore from '_stores/site.store'
import { useSiteEnableTanningFeatures } from './sites.hooks'
import { getEquipments, getAvailableEquipments } from '_services/tanning/equipments.service'
import {
  getEquipmentLampLogs,
  getEquipmentLampStatuses,
} from '_services/tanning/equipment-lamps.service'

const EQUIPMENT_LAMP_THRESHOLD = 'EQUIPMENT_LAMP_THRESHOLD'

export const useEquipmentFCMAlerts = () => {
  const currentSite = useSiteStore((state) => state.currentSite)
  const queryClient = useQueryClient()
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const { data: tanningData } = useSiteEnableTanningFeatures()
  const hasTanningFeatures = tanningData?.adminConfigurations?.enableTanningFeatures ?? false

  const fcmTokenRef = useRef(null)
  const prevSubSiteIdRef = useRef(null)

  // Subscribe/unsubscribe to equipment alerts topic when site or tanning features change
  useEffect(() => {
    const siteId = currentSite?.siteId
    if (!hasTanningFeatures || !siteId) {
      return
    }

    const setup = async () => {
      if (!import.meta.env.VITE_FCM_VAPID_KEY) {
        return
      }

      if (!fcmTokenRef.current) {
        const { getMessaging, getToken } = await import('firebase/messaging')
        fcmTokenRef.current = await getFCMToken(auth.app, import.meta.env.VITE_FCM_VAPID_KEY, {
          getMessaging,
          getToken,
        })
      }

      const fcmToken = fcmTokenRef.current
      if (!fcmToken || prevSubSiteIdRef.current === siteId) {
        return
      }

      if (prevSubSiteIdRef.current) {
        await unsubscribeFromStaffEquipmentAlerts(fcmToken, prevSubSiteIdRef.current)
      }
      await subscribeToStaffEquipmentAlerts(fcmToken, siteId)
      prevSubSiteIdRef.current = siteId
    }

    setup()

    return () => {
      const fcmToken = fcmTokenRef.current
      const subSiteId = prevSubSiteIdRef.current
      if (fcmToken && subSiteId) {
        unsubscribeFromStaffEquipmentAlerts(fcmToken, subSiteId)
        prevSubSiteIdRef.current = null
      }
    }
  }, [hasTanningFeatures, currentSite?.siteId])

  // Listen for equipment status updates — invalidate queries and show a toast
  useEffect(() => {
    const siteId = currentSite?.siteId
    if (!hasTanningFeatures || !siteId) {
      return
    }

    const channel = new BroadcastChannel('fcm-notifications')

    const handleMessage = (event) => {
      if (event.data?.type !== 'FCM_NOTIFICATION') {
        return
      }
      const fcmPayload = event.data.data
      const fcmData = fcmPayload?.data || fcmPayload
      if (fcmData?.siteId && fcmData.siteId !== siteId) {
        return
      }
      if (fcmData?.type === EQUIPMENT_LAMP_THRESHOLD) {
        queryClient.invalidateQueries([getEquipmentLampStatuses.key])
        queryClient.invalidateQueries([getEquipmentLampLogs.key])
        openToast({
          title: t(
            `checkIn:equipments.lampHours.alert.${fcmData.state === 'DUE' ? 'DUE' : 'WARNING'}`
          ),
          description: t('checkIn:equipments.lampHours.alert.description'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.INFO,
          horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
        })
        return
      }
      if (fcmData?.type !== 'EQUIPMENT_STATUS_UPDATED') {
        return
      }
      const statusKey = (fcmData.status ?? '').toLowerCase()
      if (statusKey === 'delay') {
        return
      }
      queryClient.invalidateQueries([getEquipments.key])
      queryClient.invalidateQueries([getAvailableEquipments.key])
      const statusLabel = t(`checkIn:equipments.manageEquipments.statusLabels.${statusKey}`, {
        defaultValue: statusKey.replace(/_/g, ' '),
      })
      openToast({
        title: t('checkIn:equipments.manageEquipments.statusUpdatedTitle'),
        description: t('checkIn:equipments.manageEquipments.statusUpdatedDescription', {
          status: statusLabel,
        }),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
      })
    }

    channel.addEventListener('message', handleMessage)
    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [hasTanningFeatures, currentSite?.siteId, queryClient, openToast, t])
}
